import type { FetchQuoteParams } from 'actions/InsuranceActions'
import { createSelector } from 'reselect'
import {
  getCheckoutInsuranceDates,
  getCheckoutInsuranceBookingProtectionTravellers,
  getCheckoutInsuranceTravellers,
  getCheckoutInsuranceCountries,
} from './insurance'
import getCheckoutInsuranceCoverAmount from './getCheckoutInsuranceCoverAmount'
import { checkoutHasPreselectedUpstreamInsurance, getInsuranceItemByProductType } from '../view/insurance'
import { isCruiseCheckout } from '../view/cruise'
import { checkoutContainsFlightsWithEmbeddedInsurance } from '../view/flights'
import { selectInsurancePriceIncreaseIsVariant } from 'lib/insurance/insurancePriceIncreaseExperiment'

/**
 * Whether this checkout quote should request the +10% insurance price increase.
 *
 * Derived from state (not the useExperiment hook) so the identical value can be folded into the
 * quote params below and used by BOTH the fetch hooks and every checkout read-selector — keeping
 * the write and read quote cache keys identical for treatment users. Only treatment-variant
 * customers making a fresh insurance decision (no protection pre-selected upstream) get the flag;
 * everyone else gets `undefined`, leaving their params/key/payload identical to the base price.
 */
export const getCheckoutInsuranceApplyPriceIncrease = createSelector(
  (state: App.State) => selectInsurancePriceIncreaseIsVariant(state),
  (state: App.State) => checkoutHasPreselectedUpstreamInsurance(state),
  (isVariant, hasPreselectedUpstreamInsurance): true | undefined =>
    isVariant && !hasPreselectedUpstreamInsurance ? true : undefined,
)

/**
 * Retrieves the list of parameters to fetch a quote that can be derived
 * from the checkout state, this includes:
 * - Dates
 * - Travellers
 * - Coverage amount
 * - Destination countries
 */
function buildCheckoutInsuranceQuoteParamsSelector(
  getTravellers: (state: App.State) => App.Occupants,
  getInsuranceItem: (state: App.State) => App.Checkout.InsuranceItem | undefined,
  getIsFlightBooking: (state: App.State) => boolean = () => false,
) {
  return createSelector(
    (state: App.State) => getCheckoutInsuranceDates(state),
    (state: App.State) => getTravellers(state),
    (state: App.State) => getCheckoutInsuranceCoverAmount(state),
    (state: App.State) => getCheckoutInsuranceCountries(state),
    (state: App.State) => state.checkout.cart.cartId,
    (state: App.State) => state.checkout.cart.currencyCode,
    (state: App.State) => getInsuranceItem(state),
    (state: App.State) => isCruiseCheckout(state),
    (state: App.State) => getIsFlightBooking(state),
    (state: App.State) => getCheckoutInsuranceApplyPriceIncrease(state),
    (dates, travellers, coverAmount, destinationCountries, cartId, currency, item, isCruise, isFlightBooking, applyPriceIncrease): Partial<FetchQuoteParams> => {
      return {
        startDate: dates?.startDate,
        endDate: dates?.endDate,
        travellers,
        coverAmount,
        isCruise,
        cartId,
        currency,
        destinationCountries,
        numberOfSeniors: item?.numberOfSeniors ?? 0,
        isFlightBooking,
        applyPriceIncrease,
      }
    },
  )
}

const getCheckoutInsuranceQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'travel-insurance'),
)

export const getCheckoutInsuranceBookingProtectionQuoteParams = buildCheckoutInsuranceQuoteParamsSelector(
  getCheckoutInsuranceBookingProtectionTravellers,
  (state: App.State) => getInsuranceItemByProductType(state, 'booking-protection'),
  (state: App.State) => checkoutContainsFlightsWithEmbeddedInsurance(state),
)

export function getCheckoutInsuranceQuoteParamsForType(
  quoteParams: Partial<FetchQuoteParams>,
  bookingProtectionQuoteParams: Partial<FetchQuoteParams>,
  type: App.InsuranceProductType | undefined,
): Partial<FetchQuoteParams> {
  return type === 'booking-protection' ? bookingProtectionQuoteParams : quoteParams
}

export function getCheckoutInsuranceQuoteParamsByType(
  state: App.State,
  type?: App.InsuranceProductType,
): Partial<FetchQuoteParams> {
  if (type === 'booking-protection') {
    return getCheckoutInsuranceBookingProtectionQuoteParams(state)
  }

  return getCheckoutInsuranceQuoteParams(state)
}

export default getCheckoutInsuranceQuoteParams
